import {useEffect, useMemo, useState} from "react";
import {useIntegrator} from "../../integrator.context";
import {useApp} from "../../app.context";
import {get} from "../../utils/http.client";
import {apiBindings} from "../../utils/constants";
import {RemoteConstantsModel} from "../../models/remoteConstants.model";
import {BranchDiff, diffBranches} from "../../utils/branchDiff";
import SectionDiffView from "./sectionDiffView";
import {buildSectionActions, SectionActionSummary} from "./sectionActions";
import SelectableLabel from "../../elements/selectable.label";

interface DiffViewProps {
    onClose(): void;
}

// Persist the last "Other" choice keyed by the Current branch, so reopening
// the dialog while still on the same branch lands on the same comparison
// you were last using. Switching to a different Current branch resets to
// the default. Stored values that are no longer valid (branch deleted,
// renamed, etc.) gracefully fall back to the first candidate.
const STORAGE_KEY_PREFIX = 'rift-diff-other-';

function loadRememberedOther(currentBranch: string, candidates: string[]): string {
    if (typeof window === 'undefined') return candidates[0] ?? '';
    const stored = localStorage.getItem(STORAGE_KEY_PREFIX + currentBranch);
    if (stored && candidates.includes(stored)) return stored;
    return candidates[0] ?? '';
}

function DiffView(props: DiffViewProps) {
    const {branch, branches, getBranchVersionNumber, state} = useIntegrator();
    const {getLocalizationValue} = useApp();

    const otherCandidates = branches.map(b => b.name).filter(n => n !== branch);
    const [target, setTarget] = useState<string>(() => loadRememberedOther(branch, otherCandidates));

    function chooseTarget(next: string) {
        setTarget(next);
        if (typeof window !== 'undefined') {
            if (next) localStorage.setItem(STORAGE_KEY_PREFIX + branch, next);
            else localStorage.removeItem(STORAGE_KEY_PREFIX + branch);
        }
    }
    const [targetData, setTargetData] = useState<RemoteConstantsModel | undefined>(undefined);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | undefined>(undefined);

    useEffect(() => {
        if (target === '') {
            setTargetData(undefined);
            return;
        }
        let cancelled = false;
        (async () => {
            setLoading(true);
            setError(undefined);
            const result = await get(`${apiBindings.api}getRemoteConstants?branch=${target}`, undefined);
            if (cancelled) return;
            if (!result.success) {
                setError(`Couldn't fetch '${target}'.`);
                setTargetData(undefined);
            } else {
                setTargetData(result.data as RemoteConstantsModel);
            }
            setLoading(false);
        })();
        return () => { cancelled = true; };
    }, [target]);

    const diff: BranchDiff | undefined = useMemo(() => {
        if (!targetData) return undefined;
        return diffBranches(state, targetData);
    }, [state, targetData]);

    const currentBranchLabel = `${branch} v${getBranchVersionNumber(branch)}`;

    // Action model per section — drives the summary counts and each section's
    // "Take all" bulk button. Recomputed whenever the diff or Current changes.
    const summaries: SectionActionSummary[] = useMemo(() => {
        if (!diff || !targetData) return [];
        return diff.sections.map(s => buildSectionActions(s, state, targetData, getLocalizationValue, currentBranchLabel));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [diff, targetData, state, currentBranchLabel]);

    const totals = useMemo(() => summaries.reduce(
        (acc, s) => ({total: acc.total + s.total, ready: acc.ready + s.ready, blocked: acc.blocked + s.blocked}),
        {total: 0, ready: 0, blocked: 0}), [summaries]);

    // Per-section open state, keyed by section title (titles are stable).
    const [openSections, setOpenSections] = useState<Record<string, boolean>>({});
    const anyChangedOpen = !!diff && diff.sections.some(s => !s.identical && openSections[s.title]);

    function toggleExpandAll() {
        if (!diff) return;
        if (anyChangedOpen) {
            setOpenSections({});
        } else {
            const next: Record<string, boolean> = {};
            diff.sections.forEach(s => { if (!s.identical) next[s.title] = true; });
            setOpenSections(next);
        }
    }

    return (
        <div className={'layout vertical overlay'} onClick={props.onClose}>
            <div className={'layout vertical gap box'}
                 style={{padding: 15, gap: 10, width: '80vw', height: '85vh'}}
                 onClick={e => e.stopPropagation()}>
                <div className={'layout horizontal center stretch'} style={{gap: 10}}>
                    <p className={'text header'}>Compare Branches</p>
                    <button onClick={props.onClose}>
                        <span className={'fa-solid fa-xmark'}/>
                    </button>
                </div>
                <div className={'layout horizontal center gap'} style={{gap: 15}}>
                    <p>
                        <strong>Current:</strong> {branch} v{getBranchVersionNumber(branch)}
                    </p>
                    <span className={'fa-solid fa-arrow-right'}/>
                    <p><strong>Other:</strong></p>
                    <SelectableLabel name={''} value={target} options={otherCandidates} literalOptions
                                     onChange={v => chooseTarget(String(v))}/>
                    {target !== '' && <p style={{color: 'rgba(255,255,255,0.66)'}}>v{getBranchVersionNumber(target)}</p>}
                </div>
                {!loading && !error && diff && targetData && (
                    <div className={'layout horizontal center'} style={{gap: 14, padding: '4px 2px', borderBottom: '1px solid var(--overlay-color)'}}>
                        <span style={{fontSize: 13}}>
                            <strong>{totals.total}</strong> difference{totals.total === 1 ? '' : 's'}
                        </span>
                        <span style={{fontSize: 13, color: '#7ad17a'}}>{totals.ready} ready</span>
                        {totals.blocked > 0 && <span style={{fontSize: 13, color: '#ffaa66'}}>{totals.blocked} blocked</span>}
                        <span style={{flexGrow: 1}}/>
                        {totals.total > 0 && (
                            <button onClick={toggleExpandAll} style={{padding: '2px 9px', fontSize: 12}}>
                                {anyChangedOpen ? 'Collapse all' : 'Expand changed'}
                            </button>
                        )}
                    </div>
                )}
                <div className={'layout vertical gap'} style={{flexGrow: 1, overflowY: 'auto', gap: 6}}>
                    {loading && <p>Loading…</p>}
                    {error && <p style={{color: '#ff6666'}}>{error}</p>}
                    {!loading && !error && diff && targetData && diff.sections.map((section, i) => (
                        <SectionDiffView key={section.title} section={section} targetData={targetData}
                                         actionSummary={summaries[i]}
                                         open={openSections[section.title] ?? false}
                                         onOpenChange={v => setOpenSections(prev => ({...prev, [section.title]: v}))}/>
                    ))}
                </div>
            </div>
        </div>
    );
}

export default DiffView;
